import { createContext, useContext } from 'react';
// Type-only: erased at compile time, so this module pulls no web3 code into
// whatever imports it. The wagmi-backed provider is in WalletProvider.tsx.
import type { JsonRpcSigner } from 'ethers';

export interface WalletContextType {
  isConnected: boolean;
  address: string | undefined;
  signer: JsonRpcSigner | null;
  chainId: number | undefined;
  connect: () => Promise<void>;
  disconnect: () => void;
  isLoading: boolean;
}

export const WalletContext = createContext<WalletContextType | undefined>(undefined);

/**
 * Inert state for runtimes where the Web3 extension is not loaded at all.
 * Tenants with crypto disabled never mount WalletProvider (see
 * providers/Web3Providers.tsx), so a wallet-aware component rendering there
 * should report "no wallet" rather than crash the page. connect() rejects
 * instead of silently no-op'ing — a caller awaiting a connection deserves
 * to know it cannot happen here.
 */
const WALLET_UNAVAILABLE: WalletContextType = {
  isConnected: false,
  address: undefined,
  signer: null,
  chainId: undefined,
  connect: async () => {
    throw new Error('Wallet features are not enabled for this tenant');
  },
  disconnect: () => {},
  isLoading: false,
};

export function useWallet() {
  const context = useContext(WalletContext);
  return context === undefined ? WALLET_UNAVAILABLE : context;
}
